/**
 * Shared font size utilities for embedded mode (ConnectWise pod).
 * 
 * When components are embedded in iframes, font sizes are reduced by a consistent
 * amount to match the parent application's text size.
 */

export const FONT_SIZES = {
  HEADER: 18,
  SUBHEADER: 16,
  BODY: 14,
  MEDIUM: 13,
  SMALL: 12,
  XSMALL: 11,
  INPUT: 15,
} as const;

/**
 * Calculate font size based on embedded state.
 * Reduces font size when embedded (matching ConnectWise pod text size).
 * 
 * @param baseSize - Base font size in pixels
 * @param isEmbedded - Whether component is in embedded context (ConnectWise pod)
 * @returns Adjusted font size (minimum 8px)
 */
export const getFontSize = (baseSize: number, isEmbedded: boolean): number => {
   return isEmbedded ? Math.max(baseSize - 2, 8) : baseSize;
};

/**
 * Get font sizes object for embedded/non-embedded contexts.
 * 
 * @param isEmbedded - Whether component is in embedded context
 * @returns Object with all font sizes adjusted for context
 */
export const getFontSizes = (isEmbedded: boolean) => {
  return {
    header: getFontSize(FONT_SIZES.HEADER, isEmbedded),
    subheader: getFontSize(FONT_SIZES.SUBHEADER, isEmbedded),
    body: getFontSize(FONT_SIZES.BODY, isEmbedded),
    medium: getFontSize(FONT_SIZES.MEDIUM, isEmbedded),
    small: getFontSize(FONT_SIZES.SMALL, isEmbedded),
    xsmall: getFontSize(FONT_SIZES.XSMALL, isEmbedded),
    input: getFontSize(FONT_SIZES.INPUT, isEmbedded),
  };
};
